import React, { useState } from 'react';
import { Asset, WarningIcon } from '@clearcover/atlas';
import { VehicleInspectionRequest as VehicleInspectionRequestType } from '@clearcover/core-app-types';
import cx from 'classnames';
import { Asset as SharedAsset } from 'Shared/components';
import { STATUS_ENUM, REASON_ENUM } from '../enums';
import { ChangeStatusType } from '../types';
import { isPresent } from 'Shared/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { transformEnum } from 'Policies/utils/helpers/helpers';
import { VehicleInspectionRequestDetails } from '../VehicleInspectionRequestDetails';

import styles from './styles.module.css';

export const enumToText = (enumValue: string, type: 'STATUS' | 'REASON') => {
  switch (type) {
    case 'STATUS':
      return (
        (STATUS_ENUM as { [key: string]: string })[enumValue] ||
        transformEnum(enumValue)
      );
    case 'REASON':
      return (
        (REASON_ENUM as { [key: string]: string })[enumValue] ||
        transformEnum(enumValue)
      );
    default:
      return transformEnum(enumValue);
  }
};

interface VehicleInspectionRequestProps {
  vehicleRequest: VehicleInspectionRequestType;
  onChangeStatus: ChangeStatusType;
  onRemediate: () => void;
}

export const VehicleInspectionRequest: React.FC<
  VehicleInspectionRequestProps
> = ({ vehicleRequest, onChangeStatus, onRemediate }) => {
  const [isOpen, toggleOpen] = useState(false);

  return (
    <div className={styles.rowContainer}>
      <li className={styles.row} key={vehicleRequest.requestId}>
        <div
          className={cx(styles.data, {
            [styles.submitted]: vehicleRequest.status === 'SUBMITTED',
            [styles.withIcon]: vehicleRequest.status === 'OVERDUE',
          })}
        >
          {vehicleRequest.status === 'OVERDUE' && (
            <Asset
              icon={WarningIcon}
              description="Warning"
              height={16}
              width={16}
              className={styles.warningIcon}
            />
          )}
          {enumToText(vehicleRequest.status, 'STATUS')}
        </div>
        <div className={styles.data}>{vehicleRequest.vehicle}</div>
        <div className={styles.data}>
          {enumToText(vehicleRequest.reason, 'REASON')}
        </div>
        <div className={styles.data}>{vehicleRequest.creationDate}</div>
        <div
          className={cx(styles.detailsToggle)}
          onClick={() => toggleOpen(!isOpen)}
        >
          {isPresent(vehicleRequest.details) && (
            <motion.div
              initial={false}
              exit="exit"
              animate={{
                transition: { ease: 'easeIn' },
                rotate: isOpen ? 180 : 0,
              }}
            >
              <SharedAsset variant="upArrow" />
            </motion.div>
          )}
        </div>
      </li>
      {isPresent(vehicleRequest.details) &&
        vehicleRequest.details.length > 0 && (
          <AnimatePresence initial={false}>
            {isOpen && (
              <motion.div
                key="content"
                initial="collapsed"
                animate="open"
                exit="collapsed"
                variants={{
                  open: {
                    opacity: 1,
                    height: 'auto',
                    transition: { ease: 'easeIn' },
                  },
                  collapsed: {
                    opacity: 0,
                    height: 0,
                    transition: { ease: 'easeOut' },
                  },
                }}
              >
                <VehicleInspectionRequestDetails
                  details={vehicleRequest.details}
                  requestId={vehicleRequest.requestId}
                  currentStatus={vehicleRequest.status}
                  requiresRemediation={vehicleRequest.requiresRemediation}
                  onChangeStatus={onChangeStatus}
                  onRemediate={onRemediate}
                />
              </motion.div>
            )}
          </AnimatePresence>
        )}
    </div>
  );
};
